import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useExpenseStore } from '../../stores/useExpenseStore';
import { useAppStore } from '../../stores/useAppStore';
import { useCurrency } from '../../contexts/CurrencyContext';
import { Pencil, Trash2, Lock, Plus, Search, X } from 'lucide-react';
import { CurrencyInput, Button } from '../ui';
import QuickAddExpense from './QuickAddExpense';
import { readParam } from '../../hooks/useUrlListState';

const ExpenseList = () => {
  const { expenses, loading, fetchExpenses, updateExpense, deleteExpense } = useExpenseStore();
  const { addNotification } = useAppStore();
  const { formatPrice } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  // draftSearch = what's typed; search = the applied query. They diverge until
  // the user hits Search / Enter, so nothing loads as you type. Both seed from
  // the URL so the view is shareable / restored on back-navigation.
  const [draftSearch, setDraftSearch] = useState(() => readParam(searchParams, 'q', ''));
  const [search, setSearch] = useState(() => readParam(searchParams, 'q', ''));
  const [dateFilter, setDateFilter] = useState(
    () => readParam(searchParams, 'date', '') || new Date().toISOString().split('T')[0]
  );
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const searchRef = useRef(null);
  const editAmountRef = useRef(null);

  const closingIdParam = searchParams.get('closing_id');

  const loadExpenses = useCallback(async () => {
    try {
      const params = {};
      if (closingIdParam) {
        params.closing_id = closingIdParam;
      } else if (dateFilter) {
        params.date = dateFilter;
      }
      if (search) params.search = search;
      params.limit = 100;

      const data = await fetchExpenses(params);

      if (data && data.length > 0 && data[0].ClosingID) {
        setIsLocked(true);
      } else {
        setIsLocked(false);
      }
    } catch {
      // Error handled in store
    }
  }, [dateFilter, search, closingIdParam, fetchExpenses]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    if (!closingIdParam) {
      searchRef.current?.focus();
    }
  }, [closingIdParam]);

  // Mirror the applied search + date into the URL (defaults omitted; today is the
  // default date, so it's left out). Left untouched while drilling into a
  // closing's expenses so its closing_id link is preserved.
  useEffect(() => {
    if (closingIdParam) return;
    const params = {};
    if (search.trim()) params.q = search.trim();
    const today = new Date().toISOString().split('T')[0];
    if (dateFilter && dateFilter !== today) params.date = dateFilter;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dateFilter, closingIdParam]);

  // Apply the typed search (Search button / Enter). loadExpenses re-runs when
  // `search` changes — nothing loads as you type.
  const handleSearch = () => {
    setSearch(draftSearch);
  };

  const clearSearch = () => {
    setDraftSearch('');
    setSearch('');
  };

  const startEdit = (expense) => {
    setEditingId(expense.ID);
    setEditAmount(String(expense.Amount));
    setEditDescription(expense.Description);
    setTimeout(() => editAmountRef.current?.focus(), 50);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditAmount('');
    setEditDescription('');
  };

  const saveEdit = async () => {
    if (!editAmount || parseFloat(editAmount) <= 0) {
      addNotification({ type: 'error', message: 'Amount must be greater than 0' });
      return;
    }
    if (!editDescription.trim()) {
      addNotification({ type: 'error', message: 'Description is required' });
      return;
    }
    try {
      await updateExpense(editingId, {
        Amount: parseFloat(editAmount),
        Description: editDescription.trim(),
      });
      addNotification({ type: 'success', message: 'Expense updated' });
      cancelEdit();
    } catch {
      addNotification({ type: 'error', message: 'Failed to update expense' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await deleteExpense(id);
      addNotification({ type: 'success', message: 'Expense deleted' });
    } catch {
      addNotification({ type: 'error', message: 'Failed to delete expense' });
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    }
    if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const total = expenses.reduce((sum, e) => sum + parseFloat(e.Amount || 0), 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="text-sm text-gray-500">Track daily shop expenses</p>
        </div>
        {!isLocked && !closingIdParam && (
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Expense
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search expenses..."
            value={draftSearch}
            onChange={(e) => setDraftSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
            style={{ fontSize: '1rem' }}
            className="w-full h-12 pl-11 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {draftSearch && (
            <button
              onClick={clearSearch}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <button
          onClick={handleSearch}
          className="h-12 px-6 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium whitespace-nowrap"
        >
          <Search className="h-5 w-5" />
          Search
        </button>
        {!closingIdParam && (
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="h-12 px-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        )}
        <div className="h-12 flex items-center px-4 bg-green-50 border border-green-200 rounded-xl text-green-700 font-semibold text-sm whitespace-nowrap">
          Total: {formatPrice(total)}
        </div>
      </div>

      {isLocked && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <Lock className="h-4 w-4 text-red-500" />
          <span className="text-sm text-red-700">
            These expenses are locked — a closing exists for this period.
          </span>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">#</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Time</th>
              {!isLocked && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && expenses.length === 0 ? (
              <tr>
                <td colSpan={isLocked ? 4 : 5} className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : expenses.length === 0 ? (
              <tr>
                <td colSpan={isLocked ? 4 : 5} className="px-4 py-8 text-center text-gray-500">
                  No expenses found.
                </td>
              </tr>
            ) : (
              expenses.map((expense, index) => (
                <tr
                  key={expense.ID}
                  className={editingId === expense.ID ? 'bg-blue-50' : 'hover:bg-gray-50'}
                >
                  <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                  <td className="px-4 py-3 text-sm">
                    {editingId === expense.ID ? (
                      <CurrencyInput
                        ref={editAmountRef}
                        value={parseFloat(editAmount) || 0}
                        onChange={(val) => setEditAmount(String(val))}
                        min={0}
                        showZero
                        className="w-24 px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        onKeyDown={handleEditKeyDown}
                      />
                    ) : (
                      <span className="font-semibold text-gray-900">{formatPrice(expense.Amount)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {editingId === expense.ID ? (
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <span className="text-gray-700">{expense.Description}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(expense.CreatedDate).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  {!isLocked && (
                    <td className="px-4 py-3 text-right">
                      {editingId === expense.ID ? (
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={saveEdit}>
                            Save
                          </Button>
                          <Button variant="ghost" size="sm" onClick={cancelEdit}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-3">
                          <button
                            onClick={() => startEdit(expense)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(expense.ID)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <QuickAddExpense
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          loadExpenses();
        }}
      />
    </div>
  );
};

export default ExpenseList;
